// Admin reset-password page (reached from the email link with ?token=...)
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock, ArrowRight, Shield } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { formatApiError } from "@/lib/adminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!token) return toast.error("This reset link is missing its token.");
        if (pw.length < 8) return toast.error("Password must be at least 8 characters.");
        if (pw !== pw2) return toast.error("Passwords do not match.");
        setBusy(true);
        try {
            await axios.post(`${API}/admin/auth/reset-password`, { token, new_password: pw });
            toast.success("Password updated. Please sign in.");
            navigate("/admin/login", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0b1f22] text-ivory flex items-center justify-center px-6 py-12">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.32em] text-gold">
                        <Shield size={12} /> Serenity Cove · Staff
                    </div>
                    <h1 className="font-display text-4xl text-ivory mt-4">Choose a new password</h1>
                    <p className="text-ivory/60 text-sm mt-3">At least 8 characters. Something memorable but not obvious.</p>
                </div>
                <form onSubmit={submit} className="bg-white/[0.04] border border-white/10 backdrop-blur-md rounded-2xl p-8" data-testid="admin-reset-form">
                    {["new", "confirm"].map((k) => (
                        <label key={k} className={k === "confirm" ? "block mt-5" : "block"}>
                            <span className="text-[10px] uppercase tracking-[0.22em] text-ivory/60">{k === "new" ? "New password" : "Confirm new password"}</span>
                            <div className="mt-2 flex items-center gap-3 border border-white/15 rounded-lg px-4 py-3 bg-black/20 focus-within:border-gold/50 transition-colors">
                                <Lock size={15} className="text-ivory/40" />
                                <input type="password" required minLength={8} value={k === "new" ? pw : pw2} onChange={(e) => (k === "new" ? setPw(e.target.value) : setPw2(e.target.value))}
                                    className="bg-transparent outline-none w-full text-ivory text-sm" placeholder="••••••••" />
                            </div>
                        </label>
                    ))}
                    <button type="submit" disabled={busy} className="mt-7 w-full bg-gold text-navy px-6 py-3.5 rounded-full text-[11px] uppercase tracking-[0.24em] font-bold hover:bg-gold/90 inline-flex items-center justify-center gap-2 disabled:opacity-60">
                        {busy ? "Saving…" : (<>Save new password <ArrowRight size={13} /></>)}
                    </button>
                    <div className="mt-6 text-center">
                        <Link to="/admin/login" className="text-[11px] uppercase tracking-[0.2em] text-ivory/60 hover:text-gold">← Back to sign in</Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
